// ---------------------------------------------------------------------------
// Advance conditions: what makes a step consider itself done.
//
// A step lists several; the first one to fire wins. That redundancy is
// deliberate. "Click Add Member" should also advance for the user who reached
// the same dialog from a keyboard shortcut, from the mobile bottom nav, or
// because it was already open when the guide arrived.
//
//   { type: 'click',  target:   ANCHOR.membersAdd }
//   { type: 'dom',    selector: ANCHOR.memberForm }
//   { type: 'event',  name:     COACH_EVENT.memberCreated }
//   { type: 'route',  path:     PATH_ADMIN.members.add }        <- provider
//   { type: 'readiness', key:   'working_hours' }               <- Phase 1
//
// 'route' and 'readiness' are evaluated by the provider, which owns the router
// location and the readiness map. Everything else is wired here.
// ---------------------------------------------------------------------------

import { coach } from '../coachBus';
import { waitForTarget } from './dom';

export const PROVIDER_OWNED = new Set(['route', 'readiness']);

/**
 * Wire up every condition this module owns.
 *
 * @param {Array} conditions - step.advanceOn
 * @param {(cond) => void} onAdvance - called once, with the winning condition
 * @returns {() => void} cleanup
 */
export function subscribeAdvance(conditions, onAdvance) {
  const cleanups = [];
  let fired = false;

  const fire = (cond) => {
    if (fired) return;
    fired = true;
    // Tear down before handing control back: the handler will unmount us.
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
    onAdvance(cond);
  };

  (conditions || []).forEach((cond) => {
    if (!cond || PROVIDER_OWNED.has(cond.type)) return;

    if (cond.type === 'click' && cond.target) {
      // Capture phase: the app's own handler may unmount the element, and a
      // bubbling listener would then never see the event.
      const handler = (e) => {
        const el = e.target instanceof Element ? e.target : null;
        if (el && el.closest(cond.target)) fire(cond);
      };
      document.addEventListener('click', handler, true);
      cleanups.push(() => document.removeEventListener('click', handler, true));
      return;
    }

    if (cond.type === 'dom' && cond.selector) {
      const cancel = waitForTarget(
        cond.selector,
        (found) => { if (found) fire(cond); },
        { timeout: cond.timeout || 5 * 60 * 1000 } // effectively "until the step ends"
      );
      cleanups.push(cancel);
      return;
    }

    if (cond.type === 'event' && cond.name) {
      // A save that landed while the target was still painting still counts.
      if (coach.consumeRecent(cond.name)) {
        fire(cond);
        return;
      }
      cleanups.push(coach.subscribe(cond.name, () => fire(cond)));
    }
  });

  return () => {
    cleanups.forEach((fn) => fn());
    cleanups.length = 0;
  };
}
